import { useMutation, useQueryClient } from "@tanstack/react-query";
import { workflowKeys } from "./workflow.options";
import { workflowService } from "./workflow.service";
import type {
	CreateWorkflowRuleInfo,
	EditWorkflowRuleVersionInfo,
} from "./workflow.types";

export const useCreateWorkflowRule = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			payload,
			organizationId,
		}: {
			payload: CreateWorkflowRuleInfo;
			organizationId?: string;
		}) => {
			return workflowService.createWorkflowRule(payload, { organizationId });
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: workflowKeys.rules() });
		},
		onError: (error) => {
			console.error("Failed to create workflow rule:", error);
		},
	});
};

export const useArchiveWorkflowRule = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			ruleId,
			organizationId,
		}: {
			ruleId: string;
			organizationId?: string;
		}) => workflowService.archiveWorkflowRule(ruleId, { organizationId }),
		onSuccess: (_data, { ruleId }) => {
			queryClient.invalidateQueries({ queryKey: workflowKeys.rules() });
			queryClient.invalidateQueries({
				queryKey: workflowKeys.ruleDetail(ruleId),
			});
		},
	});
};

export const useActivateWorkflowRuleVersion = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			ruleId,
			versionId,
			organizationId,
		}: {
			ruleId: string;
			versionId: string;
			organizationId?: string;
		}) =>
			workflowService.activateWorkflowRuleVersion(ruleId, versionId, {
				organizationId,
			}),
		onSuccess: (_data, { ruleId }) => {
			queryClient.invalidateQueries({ queryKey: workflowKeys.rules() });
			queryClient.invalidateQueries({
				queryKey: workflowKeys.ruleDetail(ruleId),
			});
		},
	});
};

export const useCreateDraftVersion = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			ruleId,
			organizationId,
		}: {
			ruleId: string;
			organizationId?: string;
		}) => workflowService.createWorkflowRuleVersion(ruleId, { organizationId }),
		onSuccess: (_data, { ruleId }) => {
			queryClient.invalidateQueries({ queryKey: workflowKeys.rules() });
			queryClient.invalidateQueries({
				queryKey: workflowKeys.ruleDetail(ruleId),
			});
		},
	});
};

/**
 * Save edits to an existing draft workflow rule.
 * Sequentially updates metadata (name/description), the version payload,
 * and optionally the sub-organization scopes.
 */
export const useEditWorkflowRule = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			ruleId,
			versionId,
			organizationId,
			meta,
			version,
			subOrganizationIds,
			entityTypeId,
		}: {
			ruleId: string;
			versionId: string;
			organizationId?: string;
			meta: { name: string; description?: string | null };
			version: EditWorkflowRuleVersionInfo;
			/** When provided, replaces the rule's scopes with one entry per sub-org ID. */
			subOrganizationIds?: string[];
			/** The entity-type ID (jobTypeId, ticketTypeId, etc.) used for scope entries. */
			entityTypeId?: string;
		}) => {
			await workflowService.updateWorkflowRule(ruleId, meta, {
				organizationId,
			});
			const versionResult = await workflowService.updateWorkflowRuleVersion(
				ruleId,
				versionId,
				version,
				{ organizationId },
			);
			if (subOrganizationIds !== undefined) {
				const scopes = subOrganizationIds.map((subOrganizationId) => ({
					typeId: entityTypeId ?? null,
					subOrganizationId,
					enabled: true,
				}));
				await workflowService.updateWorkflowRuleScopes(ruleId, scopes, {
					organizationId,
				});
			}
			return versionResult;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: workflowKeys.rules() });
		},
		onError: (error) => {
			console.error("Failed to edit workflow rule:", error);
		},
	});
};

export const useDeleteWorkflowRule = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			ruleId,
			organizationId,
		}: {
			ruleId: string;
			organizationId?: string;
		}) => workflowService.deleteWorkflowRule(ruleId, { organizationId }),
		onSuccess: (_data, { ruleId }) => {
			queryClient.invalidateQueries({ queryKey: workflowKeys.rules() });
			queryClient.invalidateQueries({
				queryKey: workflowKeys.ruleDetail(ruleId),
			});
		},
	});
};
